import { useState, FormEvent } from 'react'
import { Link, useLocation } from 'react-router-dom'
import authService from '../../services/authService'

export const ResetPasswordPage = () => {
  const location = useLocation()
  const emailFromState = (location.state as { email?: string })?.email || ''

  const [step, setStep] = useState<'code' | 'password'>('code')
  const [email, setEmail] = useState(emailFromState)
  const [code, setCode] = useState('')
  const [password, setPassword] = useState('')
  const [passwordConfirm, setPasswordConfirm] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState(false)

  const handleVerifyCode = async (e: FormEvent) => {
    e.preventDefault()
    setError('')
    setIsLoading(true)

    try {
      await authService.verifyResetCode({ email, code })
      setStep('password')
    } catch (err: any) {
      setError(
        err.response?.data?.error ||
        err.response?.data?.detail ||
        'Código inválido ou expirado.'
      )
    } finally {
      setIsLoading(false)
    }
  }

  const handleResetPassword = async (e: FormEvent) => {
    e.preventDefault()
    setError('')

    if (password !== passwordConfirm) {
      setError('As senhas não conferem.')
      return
    }

    if (password.length < 8) {
      setError('A senha deve ter no mínimo 8 caracteres.')
      return
    }

    setIsLoading(true)

    try {
      await authService.confirmPasswordReset({
        email,
        code,
        new_password: password,
      })
      setSuccess(true)
    } catch (err: any) {
      setError(
        err.response?.data?.error ||
        err.response?.data?.detail ||
        'Código inválido ou expirado. Solicite um novo código.'
      )
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 px-4">
      <div className="max-w-md w-full">
        {/* Logo */}
        <div className="text-center mb-8">
          <img
            src="/logo-jonnpo.png"
            alt="Jonnpo"
            className="h-14 mx-auto mb-3 dark:hidden"
          />
          <img
            src="/logo-jonnpo-dark.png"
            alt="Jonnpo"
            className="h-14 mx-auto mb-3 hidden dark:block"
          />
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
            Ownership
          </h1>
        </div>

        {/* Card */}
        <div className="bg-white dark:bg-gray-800 rounded-card shadow-lg p-8">
          {success ? (
            <>
              <div className="text-center">
                <div className="mx-auto w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mb-4">
                  <svg className="w-8 h-8 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-3">
                  Senha Redefinida
                </h2>
                <p className="text-gray-600 dark:text-gray-400 text-sm mb-6">
                  Sua senha foi alterada com sucesso. Agora você pode fazer login com a nova senha.
                </p>
              </div>

              <Link
                to="/login"
                className="block w-full py-3 px-4 bg-primary hover:bg-primary-hover text-white font-medium rounded-btn transition-colors text-center"
              >
                Ir para o Login
              </Link>
            </>
          ) : step === 'code' ? (
            <>
              <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-2">
                Verificar Código
              </h2>
              <p className="text-gray-600 dark:text-gray-400 text-sm mb-6">
                Informe seu email e o código de 6 dígitos que você recebeu.
              </p>

              {error && (
                <div className="mb-4 p-3 bg-danger/10 border border-danger rounded-btn text-danger text-sm">
                  {error}
                </div>
              )}

              <form onSubmit={handleVerifyCode}>
                <div className="mb-4">
                  <label
                    htmlFor="email"
                    className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                  >
                    Email
                  </label>
                  <input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-btn bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent"
                    placeholder="seu@email.com"
                    required
                  />
                </div>

                <div className="mb-6">
                  <label
                    htmlFor="code"
                    className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                  >
                    Código de Verificação
                  </label>
                  <input
                    id="code"
                    type="text"
                    inputMode="numeric"
                    value={code}
                    onChange={(e) => {
                      const val = e.target.value.replace(/\D/g, '').slice(0, 6)
                      setCode(val)
                    }}
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-btn bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent text-center text-2xl tracking-[0.3em] font-mono"
                    placeholder="000000"
                    required
                    maxLength={6}
                    autoFocus
                  />
                </div>

                <button
                  type="submit"
                  disabled={isLoading || code.length !== 6}
                  className="w-full py-3 px-4 bg-primary hover:bg-primary-hover text-white font-medium rounded-btn transition-colors disabled:opacity-50 disabled:cursor-not-allowed mb-4"
                >
                  {isLoading ? 'Verificando...' : 'Verificar Código'}
                </button>

                <Link
                  to="/esqueci-senha"
                  className="block text-center text-sm text-primary hover:text-primary-hover transition-colors mb-2"
                >
                  Solicitar novo código
                </Link>
                <Link
                  to="/login"
                  className="block text-center text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
                >
                  Voltar ao Login
                </Link>
              </form>
            </>
          ) : (
            <>
              <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-2">
                Nova Senha
              </h2>
              <p className="text-gray-600 dark:text-gray-400 text-sm mb-6">
                Digite sua nova senha abaixo.
              </p>

              {error && (
                <div className="mb-4 p-3 bg-danger/10 border border-danger rounded-btn text-danger text-sm">
                  {error}
                </div>
              )}

              <form onSubmit={handleResetPassword}>
                <div className="mb-4">
                  <label
                    htmlFor="password"
                    className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                  >
                    Nova Senha
                  </label>
                  <input
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-btn bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent"
                    placeholder="Mínimo 8 caracteres"
                    required
                    minLength={8}
                    autoFocus
                  />
                </div>

                <div className="mb-6">
                  <label
                    htmlFor="passwordConfirm"
                    className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                  >
                    Confirmar Nova Senha
                  </label>
                  <input
                    id="passwordConfirm"
                    type="password"
                    value={passwordConfirm}
                    onChange={(e) => setPasswordConfirm(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-btn bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent"
                    placeholder="Repita a nova senha"
                    required
                    minLength={8}
                  />
                </div>

                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full py-3 px-4 bg-primary hover:bg-primary-hover text-white font-medium rounded-btn transition-colors disabled:opacity-50 disabled:cursor-not-allowed mb-4"
                >
                  {isLoading ? 'Redefinindo...' : 'Redefinir Senha'}
                </button>

                <button
                  type="button"
                  onClick={() => { setStep('code'); setError('') }}
                  className="block w-full text-center text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
                >
                  Voltar
                </button>
              </form>
            </>
          )}
        </div>

        {/* Footer */}
        <p className="text-center text-sm text-gray-500 dark:text-gray-400 mt-8">
          Sistema de pontuação por atitudes
        </p>
      </div>
    </div>
  )
}

export default ResetPasswordPage
